import React from 'react';
import ApprovalStatusBadge from '../components/shared/approval-status-badge';
import configData from './featureDisplayConfig.json';
import { cdnThumb, retryOriginalOnce } from '../utils/CdnImage';

/**
 * ═══════════════════════════════════════════════════════════════
 * FEATURE DISPLAY CONFIG — Runtime resolver
 * ═══════════════════════════════════════════════════════════════
 *
 * Reads featureDisplayConfig.json (editable by admin tool)
 * and resolves format strings into render functions.
 *
 * Format keys:
 *   "link"      → clickable link (shows hostname)
 *   "status"    → ApprovalStatusBadge
 *   "shortDate" → dd/MM format
 *   "number"    → locale number
 *   "channel"   → TikTok/Facebook label
 *   "role"      → Trưởng nhóm/Thành viên
 *   "truncate"  → truncated text (60 chars)
 *   "text"      → raw value (default)
 */

// ─── Format resolvers ──────────────────────────────────────

const formatResolvers = {
  link: (val) =>
    val ? (
      <a href={val} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline truncate block max-w-[140px] text-xs">
        {(() => { try { return new URL(val).hostname; } catch { return 'Link'; } })()}
      </a>
    ) : '—',

  status: (val) => <ApprovalStatusBadge status={val} />,

  number: (val) => (val ? Number(val).toLocaleString() : '—'),

  channel: (val) => (val === 'tiktok' ? 'TikTok' : val === 'facebook' ? 'Facebook' : (val || '—')),

  role: (val) => {
    const labels = { leader: 'Trưởng nhóm', member: 'Thành viên' };
    return labels[val] || val || '—';
  },

  truncate: (val) => val ? (val.length > 60 ? val.substring(0, 60) + '...' : val) : '—',

  checklist: (val) => {
    if (!val) return '—';
    try {
      const items = typeof val === 'string' ? JSON.parse(val) : val;
      if (!Array.isArray(items)) return val?.toString()?.substring(0, 40) || '—';
      const done = items.filter(i => i.done).length;
      const total = items.length;
      const first = items[0]?.text || '';
      const preview = first.length > 20 ? first.substring(0, 20) + '…' : first;
      return (
        <span style={{ fontSize: '11px' }}>
          <span style={{ fontWeight: 600 }}>{done}/{total}</span>
          <span style={{ color: '#9ca3af', marginLeft: '4px' }}>• {preview}{total > 1 ? ` (+${total - 1})` : ''}</span>
        </span>
      );
    } catch {
      return val?.toString()?.substring(0, 40) || '—';
    }
  },

  // Ô 32px → tải bản /thumb 320px (~20-40KB) thay vì ảnh gốc (đo 15/10/2026: Họp team 13 ảnh 3,4MB → 0,24MB).
  // Bấm vào mới mở ảnh gốc (val). Thumb hỏng → thử gốc 1 lần rồi ẩn.
  image: (val) =>
    val ? (
      <img
        src={cdnThumb(val)}
        alt="Ảnh"
        loading="lazy"
        className="w-8 h-8 rounded object-cover cursor-pointer border border-gray-200 hover:opacity-80 transition-opacity"
        onClick={(e) => {
          e.stopPropagation();
          window.__showImagePopup && window.__showImagePopup(val);
        }}
        onError={(e) => { if (!retryOriginalOnce(e.target)) e.target.style.display = 'none'; }}
      />
    ) : '—',

  shortDate: null, // handled in TableView component
  text: null,      // no special rendering
};

// ─── Resolve columns: attach render function based on format ──

const resolveColumns = (columns) =>
  columns.map((col) => {
    const resolver = formatResolvers[col.format];
    if (resolver) {
      return { ...col, render: resolver };
    }
    return col;
  });

// ─── Resolve a single feature config ──────────────────────

const resolveFeature = (raw) => {
  const resolved = { ...raw };
  if (resolved.listConfig) {
    resolved.listConfig = {
      ...resolved.listConfig,
      columns: resolveColumns(resolved.listConfig.columns || []),
    };
  }
  return resolved;
};

// ─── Build resolved config map ────────────────────────────

const RESOLVED_CONFIG = {};
Object.keys(configData).forEach((key) => {
  RESOLVED_CONFIG[key] = resolveFeature(configData[key]);
});

// ─── Exports ──────────────────────────────────────────────

export const getFeatureConfig = (key) => RESOLVED_CONFIG[key] || null;

export const getAllFeatureConfigs = () =>
  Object.values(RESOLVED_CONFIG).sort((a, b) => (a.order || 99) - (b.order || 99));

export const getRawConfig = () => configData;

export default RESOLVED_CONFIG;
